import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CostsScreenValidationResult } from '../../../../../../../@types/validation-types'
import { InsertionFlowScreensProps } from '../../../../../../../@types/props-types'
import { FieldProps } from '../../../../../../../@types/types'
import { parseNumber } from '../../../../../../../services/validation/rules/validationFunctions'
import Costs from '../../Costs'
import PriceField, { PriceFieldType } from '../../../basicInfo/fields/priceField/PriceField'
import { RecommendedPriceNotification } from '../../../basicInfo/recommended-price-notification/RecommendedPriceNotification'
import AdditionalCosts from '../../fields/additionalCosts/AdditionalCosts'
import HeatingCostsWithToggle from '../../fields/heatingCostsWithToggle/HeatingCostsWithToggle'
import DepositWithToggle from '../../fields/depositWithToggle/DepositWithToggle'
import Deposit from '../../fields/deposit/Deposit'
import { NumberInput } from '../../../../../../common/inputs/numberInput/NumberInput'
import { isWebView } from '../../../../../../../utils/navigation'
import { IconS24EuroCircle24 } from '@is24/cosma-ui-icons'
import PricePeriodTypeChips from '../../fields/pricePeriodTypeChips/PricePeriodTypeChips'
import { useIsRecommendedPriceEnabled } from '../../../../UseIsRecommendedPriceEnabled'
import styles from './WebViewCostsRent.module.css'

export type WebViewCostsRentProps = {
  screenTooltipKey?: string
  showRentPeriodSelector?: boolean | undefined
  showColdRentInCosts?: boolean
}

const WebViewCostsRent: React.FC<InsertionFlowScreensProps<CostsScreenValidationResult> & WebViewCostsRentProps> = (
  props
) => {
  const { t } = useTranslation()
  const showLabelWithTooltip = isWebView()
  const isRecommendedPriceEnabled = useIsRecommendedPriceEnabled()
  const [calculatedTotal, setCalculatedTotal] = useState<number | undefined>(undefined)

  const showRentPeriodSelector = props.showRentPeriodSelector ?? false
  const showColdRentInCosts = props.showColdRentInCosts ?? true

  // Get current values from insertion data
  const baseRentValue = props.insertionData.price ? parseNumber(props.insertionData.price) : 0
  const additionalCostsValue = props.insertionData.additionalCosts ? props.insertionData.additionalCosts : 0
  const heatingCostsValue = props.insertionData.heatingCosts ? parseNumber(props.insertionData.heatingCosts) : 0
  const heatingCostsOption = props.insertionData.heatingCostsOption ?? true

  // Calculate total rent based on current values and toggle state
  useEffect(() => {
    if (baseRentValue > 0) {
      let total = baseRentValue

      if (additionalCostsValue > 0) {
        total += additionalCostsValue
      }

      // Add heating costs only if toggle is OFF and heating costs are specified
      if (!heatingCostsOption && heatingCostsValue > 0) {
        total += heatingCostsValue
      }
      setCalculatedTotal(total)
    } else {
      setCalculatedTotal(undefined)
    }
  }, [baseRentValue, additionalCostsValue, heatingCostsValue, heatingCostsOption])

  return (
    <Costs {...props} headerKey='wizard.screen.costs.mobileNativeHeader' headerIcon={<IconS24EuroCircle24 />}>
      {(costsWrapperProps: FieldProps<CostsScreenValidationResult>) => (
        <div className='grid grid-flex gutter-vertical-l'>
          <div className='grid-item one-whole'>
            {showColdRentInCosts && (
              <>
                <PriceField
                  {...costsWrapperProps}
                  priceFieldType={PriceFieldType.COLD_RENT}
                  wrapperClassName={`grid-item one-whole${isRecommendedPriceEnabled ? ' margin-bottom-m' : ''}`}
                  showLabelWithTooltip={showLabelWithTooltip}
                />
                {isRecommendedPriceEnabled && <RecommendedPriceNotification />}
              </>
            )}
            {showRentPeriodSelector && (
              <PricePeriodTypeChips {...costsWrapperProps} wrapperClassName='grid-item one-whole margin-top-l' />
            )}
            <AdditionalCosts
              {...costsWrapperProps}
              wrapperClassName={`grid-item one-whole${showColdRentInCosts ? ' margin-top-l' : ''}`}
              showLabelWithTooltip={showLabelWithTooltip}
            />

            <HeatingCostsWithToggle {...costsWrapperProps} doReset={props.doReset!} />

            <div className='grid-item one-whole margin-top-l'>
              <NumberInput
                id='calculated-total-rent'
                className={styles.whiteTextInput}
                propertyName='totalRent'
                placeholder=''
                value={calculatedTotal}
                minPrecision={0}
                maxPrecision={2}
                maxLength={14}
                affixRight='unit'
                unitRight='eur'
                disabled={true}
                errors={[]}
                doOnFieldChange={() => {}}
                label={t('wizard.screen.costs.costCalculation.totalRent')}
              />
            </div>

            {showLabelWithTooltip ? (
              <DepositWithToggle {...costsWrapperProps} doReset={props.doReset!} />
            ) : (
              <Deposit {...costsWrapperProps} />
            )}
          </div>
        </div>
      )}
    </Costs>
  )
}

export default WebViewCostsRent
